import React, { useEffect, useRef, useState } from "react";

import { useBelowBreakpoint } from "@impero-com/ui";
import { Footer } from "antd/es/layout/layout";
import { cx } from "class-variance-authority";
import { createPortal } from "react-dom";

import * as theme from "./theme.css";

interface StickyFooterProps {
    children: React.ReactNode;
    noShadow?: boolean;
}
const StickyFooter = ({ children, noShadow }: StickyFooterProps) => {
    const belowLg = useBelowBreakpoint("lg");
    const footerRef = useRef<HTMLDivElement>(null);
    const contentEndRef = useRef<HTMLDivElement>(null);
    const [hasContentBelow, setHasContentBelow] = useState(false);

    // Detects whether there is more content below the footer
    useEffect(() => {
        const target = belowLg ? contentEndRef.current : footerRef.current;
        if (!target) {
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => setHasContentBelow(entry.intersectionRatio < 1),
            { threshold: 1 }
        );
        observer.observe(target);
        return () => observer.disconnect();
    }, [belowLg]);

    const rootClassNames = cx(
        theme.stickyFooter,
        belowLg && theme.stickyFooterMobWidth,
        hasContentBelow && noShadow !== true && theme.stickyActive,
        noShadow && theme.topBorder
    );

    const content = (
        <Footer ref={footerRef} className={rootClassNames}>
            {children}
        </Footer>
    );
    // Ideally we'd place it #main-container or further down, but that will cause other layout/scroll issues on iOS.
    // The goal is to use sticky in the same way as above sm widths, but until the container layout is reworked, there will be scroll issues
    if (belowLg) {
        return (
            <>
                {createPortal(content, document.body)}
                <div ref={contentEndRef} className={theme.contentEnd} />
            </>
        );
    }
    return content;
};

export { StickyFooter };
